import React, { SyntheticEvent, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { List } from 'semantic-ui-react';
import styled from 'styled-components';

import { SubMenuItemProps, MenuItemProps } from './types';

import { iRootState, iRootDispatch } from '../store';

import { CurrentPageState } from '../store/types/temp-types';
import AuthorizationContainer from '../components/AuthorizationContainer';
import { ActionEnum } from '../enums/authz-action.enum';
import { FeatureEnum } from '../enums/authz-feature.enum';
import { UserRolesEnum } from '../enums/user-roles.enum';
import { isBackofficeTestUser } from '../util';

const permissionsMap = new Map<string, { action: string; feature: string }>([
  ['Novo Usuário', { action: ActionEnum.create, feature: FeatureEnum.users }],
  ['Usuários', { action: ActionEnum.read, feature: FeatureEnum.users }],
  ['Nova Imobiliária', { action: ActionEnum.create, feature: FeatureEnum.estates }],
  ['Imobiliárias', { action: ActionEnum.read, feature: FeatureEnum.estates }],
  ['Cotação', { action: ActionEnum.create, feature: FeatureEnum.fireinsurances }],
  ['Nova Análise', { action: ActionEnum.create, feature: FeatureEnum.analysis }],
  ['Novo Pré-cadastro', { action: ActionEnum.create, feature: FeatureEnum.preregisters }],
  ['Pré-cadastros', { action: ActionEnum.read, feature: FeatureEnum.preregisters }],
  [
    'Gerenciar responsáveis',
    { action: ActionEnum.changeResponsible, feature: FeatureEnum.claims }
  ],
  ['Sinistros ativos', { action: ActionEnum.read, feature: FeatureEnum.claims }],
  ['Carteira de imóveis', { action: ActionEnum.read, feature: FeatureEnum.estates }]
]);

type SubMenuItemBarProps = {
  first: boolean;
};

type SubMenuItemActiveWrapperProps = {
  actived: boolean;
};

const SubMenuItemWrapper = styled(List.Item)`
  position: relative;
  display: block;
  padding: ${props => (props.actived ? '0 0 0 30px' : '16px 0 16px 50px')} !important;
  background: #fff !important;
  font-size: 1rem !important;
  font-weight: 400 !important;
  color: ${props => props.theme.main.color} !important;
`;

const SubMenuItemActiveWrapper = styled.div<SubMenuItemActiveWrapperProps>`
  background-color: ${props =>
    props.actived ? props.theme.secundary.color : '#fff'} !important;
  display: flex;
  padding: 16px 0 16px 22px;
  margin-left: -2px;
`;

const SubMenuItemBar = styled.div<SubMenuItemBarProps>`
  display: block;
  position: absolute;
  left: 26px;
  top: ${props => (props.first ? '10px' : '0')};
  width: 2px;
  height: 100%;
  background: #ddd;
`;

const mapState = (state: iRootState) => ({
  currentPage: state.currentPage,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateCurrentPage: (state: CurrentPageState) =>
    dispatch.currentPage.updateCurrentPage(state)
});

type Props = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch> &
  SubMenuItemProps & {
    changeSelectedMenuBackground: (selectedItem: SubMenuItemProps) => void;
    first: boolean;
    parent: MenuItemProps;
  };

const SubMenuItem: React.FC<Props> = ({
  currentPage,
  user,
  first,
  parent,
  updateCurrentPage,
  changeSelectedMenuBackground,
  ...submenuItem
}: Props) => {
  const history = useHistory();
  const match = useRouteMatch();

  const handleClick = (event: SyntheticEvent, item: SubMenuItemProps) => {
    event.stopPropagation();

    if (item.name !== currentPage.name) {
      updateCurrentPage({
        id: item.id,
        name: item.name,
        text: item.text,
        title: item.title,
        parent
      });

      if (item.name === 'bond-insurance' && window.innerWidth < 700) {
        history.push('/pwa/new-analysis');
      } else {
        history.push(`${match.url}/${item.route}`);
      }
    }
  };

  useEffect(() => {
    if (currentPage.name === submenuItem.name) {
      changeSelectedMenuBackground(submenuItem);
    }
  }, [changeSelectedMenuBackground, currentPage.name, submenuItem]);

  const { action, feature } = permissionsMap.get(submenuItem.text) || {
    action: ActionEnum.manage,
    feature: FeatureEnum.analysis
  };

  const role = String(user?.role || '');
  const isStandaloneChargesMenuItem = submenuItem.text === 'Cobranças';
  const canAccessStandaloneChargesByRole =
    role.includes('ESTATE') ||
    role === UserRolesEnum.brokerAdmin ||
    role === UserRolesEnum.brokerAnalyst;

  const content = (
    <SubMenuItemWrapper
      id={submenuItem.id}
      className="link"
      actived={currentPage.name === submenuItem.name}
      onClick={(event: SyntheticEvent) => handleClick(event, submenuItem)}
    >
      {currentPage.name === submenuItem.name ? (
        <SubMenuItemActiveWrapper actived={currentPage.name === submenuItem.name}>
          <strong>{submenuItem.text}</strong>
        </SubMenuItemActiveWrapper>
      ) : (
        submenuItem.text
      )}

      <SubMenuItemBar first={first} />
    </SubMenuItemWrapper>
  );

  if (isStandaloneChargesMenuItem) {
    if (isBackofficeTestUser(user)) return content;
    return canAccessStandaloneChargesByRole ? content : null;
  }

  if (submenuItem.text === 'Carteira de imóveis') {
    return content;
  }

  // O perfil de testes pode navegar pelos fluxos para validá-los. As operações
  // de alteração continuam protegidas pelos formulários e pela API.
  if (isBackofficeTestUser(user)) {
    return content;
  }

  if (user.role === UserRolesEnum.estateQuotation) {
    const allowedSubmenus = [
      'fire-insurance-budget',
      'fire-insurance-budgets',
      'find-record',
      'bond-insurance',
      'bond-insurances',
      'capitalizations',
      'lender-insurances',
      'claim-estates'
    ];

    return allowedSubmenus.includes(submenuItem.name) ? content : null;
  }

  return (
    <AuthorizationContainer action={action} feature={feature}>
      {content}
    </AuthorizationContainer>
  );
};

export default connect(mapState, mapDispatch)(SubMenuItem);
